import './InlineEditField.scss';

import * as React from 'react';
import moment from 'moment-timezone';

import { IDropdownOption, KeyCodes, Spinner, SpinnerSize, TextField, IconButton, TooltipHost } from '@fluentui/react';

import { MultiSelector } from './MultiSelector';
import { css } from '../../util/Css';
import { DateTimePicker } from './DateTimePicker';

export interface InlineEditFieldProps {
  initialValues: string[];
  fieldNames: string[];
  readonly?: boolean;
  dateTime?: boolean;
  visitCode?: string;
  multiline?: boolean;
  valueNotEditing?: JSX.Element;
  options?: IDropdownOption[];
  preventSubmitOnEnter?: boolean;
  width?: number;
  onSubmit?: (newValue: any) => Promise<any> | void;
  validation?: (newValue: any) => boolean;
  validationTip?: string;
  iconClassName?: string;
}

export interface InlineEditFieldState {
  editing: boolean;
  saving: boolean;
  values: string[];
  invalid: boolean;
}

export class InlineEditField extends React.Component<InlineEditFieldProps, InlineEditFieldState> {
  constructor(props: InlineEditFieldProps) {
    super(props);
    this.state = {
      editing: false,
      saving: false,
      values: props.initialValues,
      invalid: false,
    };
  }

  public render(): JSX.Element {
    return this.state.editing ? this.renderWhileEditing() : this.renderWhileNotEditing();
  }

  public componentDidUpdate(prevProps: InlineEditFieldProps) {
    if (this.props.visitCode !== prevProps.visitCode || this.props.initialValues !== prevProps.initialValues) {
      this.setState({
        editing: false,
        saving: false,
        values: this.props.initialValues,
        invalid: false,
      });
    }
  }

  private renderWhileEditing(): JSX.Element {
    return (
      <div className="inline-edit-container">
        {this.props.options ? this.renderDropdown() : this.props.dateTime ? this.renderDateTime() : this.renderTextFields()}
        &nbsp;
        {this.state.invalid && (
          <TooltipHost content={this.props.validationTip}>
            <IconButton iconProps={{ iconName: 'Info' }} style={{ color: 'red' }} />
          </TooltipHost>
        )}
        <IconButton iconProps={{ iconName: 'Save' }} onClick={this.onSaveClicked} />
        <IconButton iconProps={{ iconName: 'Cancel' }} onClick={this.onCancelClicked} />
      </div>
    );
  }

  private renderTextFields(): JSX.Element[] {
    return this.state.values.map((value, i) => {
      return (
        <div
          key={i}
          className="text-field-container"
          style={{
            width: (this.props.width || 250) / this.state.values.length,
            display: 'inline-block',
            marginRight: 4,
          }}
        >
          <TextField autoFocus={true} value={value} multiline={this.props.multiline} onChange={(ev, v) => this.onValueChanged(i, v)} onKeyDown={this.onKeyDown} />
        </div>
      );
    });
  }

  private renderDropdown(): JSX.Element {
    return (
      <div style={{ width: this.props.width || '80%', display: 'inline-block', verticalAlign: 'middle', marginRight: 4 }}>
        <MultiSelector
          items={(this.props.options as { key: string; text: string }[]) || []}
          selectedKeys={this.state.values || []}
          onChange={(v) => this.setState({ values: v })}
          disabled={false}
          required={true}
        />
      </div>
    );
  }

  private renderDateTime(): JSX.Element {
    return (
      <div style={{ width: '70%', display: 'inline-block', verticalAlign: 'middle' }} onKeyDown={this.onKeyDown}>
        <DateTimePicker value={this.getDateTimeInitialValue()} onChange={this.onTimeChange} />
      </div>
    );
  }

  private getDateTimeInitialValue = (): moment.Moment => {
    if (!this.state.values[0]) {
      const now = moment();
      this.setState({ values: [now.format('MM/DD/YYYY h:mm a')] });
      return now;
    } else {
      return moment(this.state.values[0]);
    }
  };

  private onTimeChange = (time: moment.Moment) => {
    if (moment.isMoment(time)) {
      this.setState({ values: [time.format('MM/DD/YYYY h:mm a')] });
    } else {
      this.setState({ values: [''] });
    }
  };

  private formatDateTime(date: string): string {
    const dateTime = moment(date);
    return dateTime.format('MM/DD/YYYY h:mm A');
  }

  private onKeyDown = (e: React.KeyboardEvent<any>) => {
    if (!this.props.preventSubmitOnEnter && e.keyCode === KeyCodes.enter) {
      this.onSaveClicked();
    }
  };

  private renderWhileNotEditing(): JSX.Element {
    if (this.props.valueNotEditing) {
      return (
        <div className="inline-edit-container">
          {this.props.valueNotEditing}
          &nbsp;
          {!this.state.saving && !this.props.readonly && <IconButton iconProps={{ iconName: 'Edit' }} onClick={this.onEditClicked} />}
          {this.state.saving && <Spinner size={SpinnerSize.large} />}
        </div>
      );
    } else {
      return (
        <div className="inline-edit-container">
          {!this.state.saving && (
            <div className={css('link-button', 'edit-button', { disabled: this.props.readonly! })} tabIndex={-1} onClick={this.onEditClicked}>
              {this.props.options
                ? this.props.options
                    .filter((m) => this.state.values.some((i) => i === m.key))
                    .map((v: any) => v.text)
                    .join(', ')
                : this.props.dateTime
                ? this.state.values[0]
                  ? this.formatDateTime(this.state.values[0])
                  : ''
                : this.state.values.join(' ')}
              &nbsp;
              {!this.props.readonly && <IconButton iconProps={{ iconName: 'Edit' }} />}
            </div>
          )}
          {this.state.saving && <Spinner size={SpinnerSize.large} />}
        </div>
      );
    }
  }

  private onEditClicked = () => {
    if (!this.props.readonly) {
      this.setState({ editing: true });
    }
  };

  private onCancelClicked = () => {
    this.setState({ editing: false, values: this.props.initialValues });
  };

  private onSaveClicked = () => {
    const newValue: { [key: string]: string | string[] } = {};
    if (this.props.options) {
      newValue[this.props.fieldNames[0]] = this.state.values;
    } else {
      this.props.fieldNames.forEach((fieldName, i) => {
        newValue[fieldName] = this.state.values[i];
      });
    }

    if (this.props.validation && !this.props.validation(newValue)) {
      this.setState({ invalid: true });
      return;
    } else {
      this.setState({ invalid: false });
    }

    if (this.props.onSubmit) {
      const submitted = this.props.onSubmit(newValue);
      this.setState({ editing: false, saving: true });
      if (submitted) {
        submitted.then((success: boolean) => {
          if (success) {
            this.setState({ editing: false, saving: false });
          } else {
            this.setState({ editing: true, saving: false });
          }
        });
      } else {
        this.setState({ editing: false, saving: false });
      }
    }
  };

  private onValueChanged = (i: number, value: any) => {
    const values = this.state.values;
    values[i] = value;
    this.setState({ values });
  };
}
